import React, { Component } from "react";
import { observer } from "mobx-react";
import { AutoComplete, List, Input, Tooltip, Button } from "antd";
import { userStore } from "../../storage/user/User";
import { Jira } from "../../services";
import { CloseCircleOutlined, PlusOutlined } from "@ant-design/icons";

export type IssueSelectorMode = "search" | "list";
export type IssueSelectorType = "self" | "project" | "epics";
export type IssueSelectorBehaviour = "available" | "all";
export type Issue = {
  expand: string;
  fields: any;
  id: string;
  key: string;
  self: string;
};
type PropTypes = {
  behaviour?: IssueSelectorBehaviour;
  mode: IssueSelectorMode;
  project?: string;
  type: IssueSelectorType;
  placeholder?: string;
  value: string;
  exceptions?: string[];
  onSelect?: (selectedIssue: Issue) => void;
  onLoad?: (loadedIssues: Issue[]) => void;
  onInputChanged?: (text: string) => void;
  onMultipleSelectionChanged?: (selectedIssues: Issue[]) => void;
  style?: any;
  showDescription?: boolean;
  multiple?: boolean;
  user?: any;
  className?: string;
};

@observer
export default class IssueSelector extends Component<PropTypes> {
  state: {
    filter?: string;
    issues: Issue[];
    loading: boolean;
    selectedIssue: string;
    onSelect: (selectedIssue: Issue) => void;
    multipleSelection: Issue[];
  };

  constructor(props: any) {
    super(props);
    this.state = {
      issues: [] as Issue[],
      loading: false,
      selectedIssue: "",
      onSelect: () => {},
      multipleSelection: [],
    };
  }

  componentDidUpdate = async (prevProps: any, prevState: any) => {
    if (
      prevProps.project !== this.props.project ||
      prevProps.user !== this.props.user
    ) {
      await this.updateEpics();
    }
  };

  componentDidMount = async () => {
    /*
            TODO: Ensure the user store is mounted at this point.
        */

    await this.updateEpics();
  };

  renderSearch = () => {
    const { issues, loading } = this.state;
    const {
      placeholder,
      value,
      exceptions,
      value: filter,
      style,
      className,
    } = this.props;
    const data: Issue[] = issues;

    return (
      <AutoComplete
        style={{ width: "100%", ...style }}
        className={className}
        options={data
          .filter((item) =>
            exceptions && exceptions.length
              ? !exceptions.includes(item.key)
              : true
          )
          .filter((item) =>
            filter
              ? `${item.key} ${item.fields.summary}`
                  .toLowerCase()
                  .includes(filter.toLowerCase())
              : true
          )
          .map((item) => ({
            label: this.renderInformativeText(item),
            value: item.key,
            //text: `${item.key} - ${item.fields.summary}`
          }))}
        value={value}
        onSearch={(filter: string) => {
          if (this.props.onInputChanged) {
            this.props.onInputChanged(filter);
          }
        }}
        onSelect={(key: string) =>
          this.selectListItem(data.find((issue) => issue.key === key))
        }
        allowClear
      >
        <Input.Search
          loading={loading}
          size="large"
          placeholder={placeholder || "XXX-123"}
        />
      </AutoComplete>
    );
  };

  selectListItem = (item: Issue | undefined) => {
    if (!item) return;
    this.setState({ selectedIssue: item.key });
    this.props.onSelect && this.props.onSelect(item);
    if (this.props.multiple) {
      let { multipleSelection } = this.state;
      const alrSelection = multipleSelection.find((i) => i.key === item.key);
      if (alrSelection) {
        multipleSelection = multipleSelection.filter((i) => i.key !== item.key);
      } else {
        multipleSelection.push(item);
      }

      this.setState({
        multipleSelection,
      });

      this.props.onMultipleSelectionChanged &&
        this.props.onMultipleSelectionChanged(multipleSelection);
    }
  };

  renderListItem = (item: any) => {
    const { selectedIssue, multipleSelection } = this.state;
    const { showDescription, multiple } = this.props;
    const isSelected =
      multipleSelection.findIndex((i) => i.key === item.key) !== -1;
    return (
      <List.Item
        onClick={() => this.selectListItem(item)}
        onDoubleClick={() => {
          window.open(
            `https://etendoproject.atlassian.net/browse/${item.key}`,
            "_blank"
          );
        }}
        style={{
          backgroundColor:
            selectedIssue === item.key || isSelected ? "#151515" : undefined,
        }}
        extra={[
          multiple ? (
            <Button
              icon={isSelected ? <CloseCircleOutlined /> : <PlusOutlined />}
              shape="circle"
            />
          ) : undefined,
        ]}
      >
        <List.Item.Meta
          title={item.fields.summary}
          description={showDescription ? item.fields.description : item.key}
        />
      </List.Item>
    );
  };

  renderList = () => {
    let { issues, loading } = this.state;

    return (
      <List
        size="small"
        loading={loading}
        dataSource={this.getFilteredIssues(issues)}
        itemLayout="horizontal"
        renderItem={(item) => this.renderListItem(item)}
        bordered
        className={this.props.className}
        style={{ ...this.props.style, overflowY: "scroll" }}
      />
    );
  };

  renderMode = (mode: IssueSelectorMode) => {
    if (mode === "list") {
      return this.renderList();
    } else if (mode === "search") {
      return this.renderSearch();
    }
  };

  private renderInformativeText(item: Issue) {
    return (
      <Tooltip title={item?.fields?.summary} style={{ width: "100%" }}>
        <span>{`${item?.key} - ${item?.fields?.summary}`}</span>
      </Tooltip>
    );
  }

  private getFilteredIssues(issues: Issue[]): Issue[] {
    if (!this.props.exceptions) return issues;

    return issues.filter(
      (issue) => !this.props.exceptions?.includes(issue.key)
    );
  }

  async updateEpics() {
    const { type, project, behaviour, onLoad, user } = this.props;
    let response: any;
    this.setState({ loading: true });

    try {
      await userStore.hydrate();
      let selectedUser = user ? user : "currentUser()";

      let queryBehaviour =
        behaviour !== "available"
          ? ""
          : ' AND (status NOT IN ("Closed", "Resolved", "Done", "Won\'t Fix") OR (status IN ("Closed", "Resolved", "Done") AND resolutionDate >= startOfDay() AND resolutionDate <= endOfDay()))';

      if (selectedUser === "NO-ASSIGNEE") {
        selectedUser = "EMPTY";
      }

      let jql = "";

      if (type === "self") {
        jql = `issuetype NOT IN (Epic) AND assignee = ${selectedUser}${queryBehaviour}`;
      } else if (type === "project") {
        jql = `issuetype NOT IN (Epic) AND project = "${project}"${queryBehaviour}`;
      } else if (type === "epics") {
        jql = `issuetype IN (Epic) AND project = "${project}"${queryBehaviour}`;
      }

      const searchParams = {
        jql: jql,
        fields: [
          "key",
          "summary",
          "description",
          "status",
          "issuetype",
          "parent",
          "assignee",
          "priority",
          "created",
          "updated",
          "project",
        ].join(","),
        maxResults: 100,
        startAt: 0,
      };

      response = await Jira.search(searchParams);

      let { issues } = response;

      if (!Array.isArray(issues)) {
        console.error("Response doesn't contain issues array:", response);
        throw new Error("Invalid response format from JIRA API");
      }

      const validIssues = issues.filter((item: any) => {
        if (!item.fields) {
          console.warn("Issue without fields:", item);
          return false;
        }

        if (!item.key || !item.fields.summary) {
          console.warn("Issue missing key or summary:", item);
          return false;
        }

        return true;
      });

      console.log(
        `Valid issues found: ${validIssues.length} out of ${issues.length}`
      );

      const closedStatusNames = ["Closed", "Resolved", "Done", "Won't Fix"];
      const filteredIssues = validIssues.filter((item: any) => {
        const epic = item?.fields?.parent || item;
        if (!epic) return true;

        const epicStatusName = epic?.fields?.status?.name;
        return !closedStatusNames.includes(epicStatusName);
      });

      this.setState({ issues: filteredIssues, loading: false }, () => {
        console.log("Issues loaded:", filteredIssues.length);
        onLoad && onLoad(filteredIssues);
      });
    } catch (error) {
      console.error("Error updating epics:", error);

      this.setState({ loading: false, issues: [] });
    }
  }
  render() {
    const { mode } = this.props;
    return this.renderMode(mode);
  }
}
